// components/Feedback.jsx
import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { MessageSquare, Send } from "lucide-react";

const Container = styled(Paper)(({ theme }) => ({
  width: "100%",
  margin: "0 auto",
  boxShadow: theme.shadows[6],
  display: "flex",
  flexDirection: "column",
}));

const Header = styled(Box)(({ theme }) => ({
  background: "linear-gradient(90deg, #f97316, #ef4444)",
  color: "#fff",
  padding: theme.spacing(3),
}));

const HeaderRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
}));

const HeaderIcon = styled(Box)(({ theme }) => ({
  width: 48,
  height: 48,
  borderRadius: "50%",
  backgroundColor: "rgba(255, 255, 255, 0.2)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
}));

const HeaderText = styled(Box)(() => ({
  flex: 1,
}));

const HeaderTitle = styled(Typography)(() => ({
  fontWeight: 700,
  marginBottom: 4,
}));

const HeaderSubtitle = styled(Typography)(() => ({
  color: "rgba(255,255,255,0.85)",
}));

const Content = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
}));

const IntroText = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  lineHeight: 1.6,
  color: theme.palette.grey[700],
}));

const Form = styled("form")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2.5),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    "&:hover fieldset": {
      borderColor: "#f97316",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#f97316",
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#f97316",
  },
  // FIXED: Mobile label overlap issue
  "& .MuiInputLabel-outlined": {
    // Add background to label so it cuts into the border properly
    "&.MuiInputLabel-shrink": {
      backgroundColor: "white",
      paddingLeft: theme.spacing(0.5),
      paddingRight: theme.spacing(0.5),
    },
  },
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  width: "100%",
  paddingBlock: theme.spacing(1.5),
  borderRadius: theme.spacing(2),
  fontWeight: 700,
  fontSize: "1rem",
  boxShadow: theme.shadows[4],
  textTransform: "none",
  background: "linear-gradient(90deg, #f97316, #ef4444)",
  color: "#fff",
  marginTop: theme.spacing(1),
  "&:hover": {
    background: "linear-gradient(90deg, #ea580c, #dc2626)",
    boxShadow: theme.shadows[8],
  },
  "&:disabled": {
    background: theme.palette.grey[300],
    color: theme.palette.grey[500],
  },
}));

const ButtonContent = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(1),
}));

const InfoBox = styled(Box)(({ theme }) => ({
  backgroundColor: "#eff6ff",
  borderRadius: theme.spacing(2),
  padding: theme.spacing(2),
  border: "1px solid #bfdbfe",
  marginBottom: theme.spacing(3),
}));

const InfoText = styled(Typography)(() => ({
  color: "#1e40af",
  fontSize: "0.875rem",
  lineHeight: 1.6,
}));

const Feedback = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [status, setStatus] = useState({
    submitted: false,
    submitting: false,
    info: { error: false, msg: null },
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({
      submitting: true,
      submitted: false,
      info: { error: false, msg: null },
    });

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          access_key: "YOUR_WEB3FORMS_ACCESS_KEY", // Replace with your actual Web3Forms access key
          name: formData.name,
          email: formData.email,
          message: formData.message,
          from_name: "Foodie Call Feedback",
          subject: "New Feedback from Foodie Call",
        }),
      });

      const result = await response.json();

      if (result.success) {
        setStatus({
          submitting: false,
          submitted: true,
          info: {
            error: false,
            msg: "Thank you! Your feedback has been received.",
          },
        });
        setFormData({ name: "", email: "", message: "" });
      } else {
        setStatus({
          submitting: false,
          submitted: false,
          info: {
            error: true,
            msg: result.message || "Something went wrong. Please try again.",
          },
        });
      }
    } catch (error) {
      setStatus({
        submitting: false,
        submitted: false,
        info: { error: true, msg: "Network error. Please try again later." },
      });
    }
  };

  return (
    <Container>
      <Header>
        <HeaderRow>
          <HeaderIcon>
            <MessageSquare size={24} color="#fff" />
          </HeaderIcon>
          <HeaderText>
            <HeaderTitle variant="h5">Send Feedback</HeaderTitle>
            <HeaderSubtitle variant="body2">
              Help us improve Foodie Call
            </HeaderSubtitle>
          </HeaderText>
        </HeaderRow>
      </Header>

      <Content>
        {!status.submitted ? (
          <>
            <IntroText>
              We'd love to hear from you! Whether you have a suggestion, found a
              bug, or just want to share your experience with Foodie Call, your
              feedback helps us make the app better for everyone.
            </IntroText>

            <InfoBox>
              <InfoText>
                💡 <strong>Tip:</strong> All feedback is anonymous unless you
                choose to provide your email address for a response.
              </InfoText>
            </InfoBox>

            {status.info.msg && status.info.error && (
              <Alert severity="error" sx={{ marginBottom: 2 }}>
                {status.info.msg}
              </Alert>
            )}

            <Form onSubmit={handleSubmit}>
              <StyledTextField
                label="Name (optional)"
                name="name"
                value={formData.name}
                onChange={handleChange}
                variant="outlined"
                fullWidth
              />

              <StyledTextField
                label="Email (optional)"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                helperText="Only if you'd like a response"
              />

              <StyledTextField
                label="Your Feedback"
                name="message"
                value={formData.message}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                multiline
                rows={6}
                required
                helperText="Required"
              />

              <SubmitButton
                type="submit"
                variant="contained"
                disabled={status.submitting}
              >
                <ButtonContent>
                  <span>
                    {status.submitting ? "Sending..." : "Send Feedback"}
                  </span>
                  {!status.submitting && <Send size={18} />}
                </ButtonContent>
              </SubmitButton>
            </Form>
          </>
        ) : (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #f97316, #ef4444)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 24px",
                boxShadow: "0 4px 12px rgba(249, 115, 22, 0.3)",
              }}
            >
              <MessageSquare size={40} color="#fff" />
            </Box>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, mb: 2, color: "#1f2937" }}
            >
              Thank You!
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "#6b7280", mb: 4, lineHeight: 1.6 }}
            >
              Your feedback has been received. We appreciate you taking the time
              to help us improve Foodie Call!
            </Typography>
            <Button
              variant="outlined"
              onClick={() => window.close()}
              sx={{
                borderColor: "#f97316",
                color: "#f97316",
                fontWeight: 600,
                textTransform: "none",
                borderRadius: 2,
                px: 3,
                "&:hover": {
                  borderColor: "#ea580c",
                  backgroundColor: "rgba(249, 115, 22, 0.04)",
                },
              }}
            >
              Close Window
            </Button>
          </Box>
        )}
      </Content>
    </Container>
  );
};

export default Feedback;
